import { database } from '../config/backend';
import { ref, get as rtdbGet, set as rtdbSet, push, query, orderByChild, equalTo, onValue, off } from './phpDataAdapter';
import { Consulta } from '../services/mockData';

interface NotificationSettings {
  enabled: boolean;
  timesBefore: number[]; // em minutos
  sendToMobile: boolean;
  sendToWeb: boolean;
}

interface ScheduledNotification {
  id: string;
  consultaId: string;
  userId: string;
  scheduledTime: string; // ISO string
  notificationTime: string; // ISO string (quando deve ser enviada)
  sent: boolean;
  createdAt: string;
}

/**
 * Carrega configurações de notificação do usuário
 */
export async function loadNotificationSettings(userId: string): Promise<NotificationSettings | null> {
  try {
    const settingsRef = ref(database, `users/${userId}/consultationNotificationSettings`);
    const snapshot = await rtdbGet(settingsRef);
    if (snapshot.exists()) {
      return snapshot.val() as NotificationSettings;
    }
    return null;
  } catch (error) {
    console.error('Erro ao carregar configurações de notificação:', error);
    return null;
  }
}

/**
 * Agenda notificações para uma consulta baseado nas configurações do usuário
 */
export async function scheduleConsultationNotifications(
  consulta: Consulta,
  userId: string
): Promise<void> {
  try {
    const settings = await loadNotificationSettings(userId);
    if (!settings || !settings.enabled || settings.timesBefore.length === 0) {
      return;
    }

    const consultaDate = new Date(consulta.dataIso);
    const notifications: ScheduledNotification[] = [];

    // Criar uma notificação para cada tempo configurado
    for (const minutesBefore of settings.timesBefore) {
      const notificationTime = new Date(consultaDate.getTime() - minutesBefore * 60 * 1000);
      
      // Só agendar se a notificação for no futuro
      if (notificationTime > new Date()) {
        const notification: Omit<ScheduledNotification, 'id'> = {
          consultaId: consulta.id,
          userId,
          scheduledTime: consultaDate.toISOString(),
          notificationTime: notificationTime.toISOString(),
          sent: false,
          createdAt: new Date().toISOString()
        };

        const notificationRef = push(ref(database, `scheduledNotifications`));
        await rtdbSet(notificationRef, notification);
        notifications.push({ id: notificationRef.key as string, ...notification });
      }
    }

    console.log(`Agendadas ${notifications.length} notificações para consulta ${consulta.id}`);
  } catch (error) {
    console.error('Erro ao agendar notificações:', error);
  }
}

/**
 * Agenda notificações para todas as consultas futuras do usuário
 */
export async function scheduleAllFutureConsultations(userId: string): Promise<void> {
  try {
    const consultasRef = ref(database, `consultas`);
    const snapshot = await rtdbGet(consultasRef);
    
    // Buscar consultas do nutricionista que ainda não aconteceram
    const now = new Date();
    const consultas: Consulta[] = [];
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      Object.entries(data).forEach(([id, consulta]: [string, any]) => {
        if (consulta.nutritionistId === userId) {
          const consultaDate = new Date(consulta.dataIso);
          if (consultaDate > now) {
            consultas.push({ id, ...consulta } as Consulta);
          }
        }
      });
    }

    // Agendar notificações para cada consulta
    for (const consulta of consultas) {
      await scheduleConsultationNotifications(consulta, userId);
    }

    console.log(`Agendadas notificações para ${consultas.length} consultas futuras`);
  } catch (error) {
    console.error('Erro ao agendar notificações para todas as consultas:', error);
  }
}

/**
 * Verifica e envia notificações agendadas que estão no horário
 */
export async function checkAndSendScheduledNotifications(): Promise<void> {
  try {
    const now = new Date();
    const notificationsRef = ref(database, `scheduledNotifications`);
    const snapshot = await rtdbGet(notificationsRef);
    
    if (!snapshot.exists()) return;

    const data = snapshot.val();
    const notifications: ScheduledNotification[] = Object.entries(data).map(([id, notif]: [string, any]) => ({
      id,
      ...notif
    }));

    // Filtrar notificações que devem ser enviadas agora e ainda não foram enviadas
    const notificationsToSend = notifications.filter(notif => {
      const notificationTime = new Date(notif.notificationTime);
      return !notif.sent && notificationTime <= now && notificationTime >= new Date(now.getTime() - 5 * 60 * 1000); // Últimos 5 minutos
    });

    for (const notification of notificationsToSend) {
      await sendNotification(notification);
      
      // Marcar como enviada
      await rtdbSet(ref(database, `scheduledNotifications/${notification.id}/sent`), true);
      await rtdbSet(ref(database, `scheduledNotifications/${notification.id}/sentAt`), new Date().toISOString());
    }

    if (notificationsToSend.length > 0) {
      console.log(`Enviadas ${notificationsToSend.length} notificações`);
    }
  } catch (error) {
    console.error('Erro ao verificar notificações agendadas:', error);
  }
}

/**
 * Envia uma notificação para o usuário
 */
async function sendNotification(notification: ScheduledNotification): Promise<void> {
  try {
    // Buscar dados da consulta
    const consultaRef = ref(database, `consultas/${notification.consultaId}`);
    const consultaSnapshot = await rtdbGet(consultaRef);
    
    if (!consultaSnapshot.exists()) {
      console.warn(`Consulta ${notification.consultaId} não encontrada`);
      return;
    }

    const consulta = consultaSnapshot.val() as Consulta;
    const consultaDate = new Date(consulta.dataIso);
    const timeUntil = Math.round((consultaDate.getTime() - new Date().getTime()) / (60 * 1000));
    
    let timeLabel = '';
    if (timeUntil < 60) {
      timeLabel = `em ${timeUntil} minutos`;
    } else if (timeUntil < 1440) {
      timeLabel = `em ${Math.round(timeUntil / 60)} horas`;
    } else {
      timeLabel = `em ${Math.round(timeUntil / 1440)} dias`;
    }

    // Criar notificação no banco de dados
    const userNotificationRef = push(ref(database, `notifications/${notification.userId}`));
    await rtdbSet(userNotificationRef, {
      id: userNotificationRef.key,
      type: 'consultation_reminder',
      consultaId: notification.consultaId,
      title: 'Lembrete de Consulta',
      message: `Você tem uma consulta com ${consulta.pacienteNome} ${timeLabel}`,
      consultaData: consulta.dataIso,
      consultaTipo: consulta.tipo,
      read: false,
      createdAt: new Date().toISOString()
    });

    // Enviar notificação do navegador se permitido
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Lembrete de Consulta', {
        body: `Você tem uma consulta com ${consulta.pacienteNome} ${timeLabel}`,
        icon: '/favicon.ico',
        tag: `consulta-${notification.consultaId}`
      });
    }
  } catch (error) {
    console.error('Erro ao enviar notificação:', error);
  }
}

/**
 * Solicita permissão para notificações do navegador
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('Este navegador não suporta notificações');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    console.warn('Permissão de notificações negada');
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

/**
 * Inicia o monitoramento de notificações agendadas
 * Deve ser chamado quando o app inicia
 */
export function startNotificationMonitoring(): () => void {
  // Verificar notificações a cada minuto
  const interval = setInterval(() => {
    checkAndSendScheduledNotifications();
  }, 60 * 1000);

  // Verificar imediatamente
  checkAndSendScheduledNotifications();

  // Retornar função para parar o monitoramento
  return () => clearInterval(interval);
}

